import React, { FC } from 'react';
import SVGInline from 'components/SVGInline/SVGInline';
import { ToastProps, useToasts } from 'providers/useToast/ToastProvider';

import icons from 'style';
import translate from 'utils/fnTranslate';
import { RecordingType } from 'types/RecordingTypes';
import { getRecordingIcon } from 'utils/fnImages';
import {
    NotificationCloser,
    NotificationIcon,
    NotificationIconWrapper,
    NotificationMessage,
    NotificationMessageWrapper,
    NotificationTitle,
    NotificationWrapper,
    toastStates,
} from './Notification.css';

type NotificationContent = {
    title: string;
    message: string;
    ico: any;
    isHidden?: boolean; // ARIA
};

type PropTypes = Omit<ToastProps, 'children'> & {
    children: NotificationContent;
};

export const Notification: FC<PropTypes> = ({ transitionState, transitionDuration, placement, children, onDismiss }) => {
    const { title, message, ico = icons.toastSuccess, isHidden = false } = children as NotificationContent;

    return (
        <NotificationWrapper
            transitionDuration={transitionDuration}
            style={{ ...toastStates(placement)[transitionState] }}
            isHidden={isHidden}
        >
            <NotificationIconWrapper>
                <NotificationIcon svg={ico} />
            </NotificationIconWrapper>
            <NotificationMessageWrapper role="alert">
                <NotificationTitle data-test-id={'notification-title'}>{title}</NotificationTitle>
                <NotificationMessage>{message}</NotificationMessage>
            </NotificationMessageWrapper>
            <NotificationCloser>
                <SVGInline svg={icons.closeIconNew} onClick={onDismiss} />
            </NotificationCloser>
        </NotificationWrapper>
    );
};

export const useTransactionSuccessNotification = () => {
    const { addToast } = useToasts();

    return (callback: () => void) => {
        addToast(
            {
                title: translate('NOTIFICATION_PURCHASE_SUCCESS_TITLE'),
                message: translate('NOTIFICATION_SERVICE_ACTIVATED_BODY'),
            } as unknown as React.ReactNode,
            {
                appearance: 'success',
                autoDismiss: true,
            },
            callback
        );
    };
};

export const useServiceActivatedNotification = () => {
    const { addToast } = useToasts();

    return () => {
        addToast(
            {
                title: translate('NOTIFICATION_SERVICE_ACTIVATED_TITLE'),
                message: translate('NOTIFICATION_SERVICE_ACTIVATED_BODY'),
            } as unknown as React.ReactNode,
            {
                appearance: 'success',
                autoDismiss: true,
            }
        );
    };
};

export const useRecordingNotification = () => {
    const { addToast } = useToasts();

    const getTitle = (type: RecordingType, action: 'ADD' | 'REMOVE', err: boolean): string => {
        if (!err) {
            switch (type) {
                case 'RECORDED':
                    return translate(action === 'ADD' ? 'NOTIFICATION_RECORDING_ADDED' : 'NOTIFICATION_RECORDING_DELETED');
                case 'ONGOING':
                    return translate(action === 'ADD' ? 'NOTIFICATION_RECORDING_STARTED' : 'NOTIFICATION_RECORDING_STOPPED');
                case 'SCHEDULED':
                    return translate(action === 'ADD' ? 'NOTIFICATION_RECORDING_SCHEDULED' : 'NOTIFICATION_RECORDING_CANCELED');
                default:
                    return null;
            }
        } else {
            switch (type) {
                case 'RECORDED':
                    return translate(action === 'ADD' ? 'RECORDING_NOTIFICATION_SCHEDULE_ERROR' : 'RECORDING_NOTIFICATION_DELETE_ERROR');
                case 'ONGOING':
                    return translate(action === 'ADD' ? 'RECORDING_NOTIFICATION_SCHEDULE_ERROR' : 'RECORDING_NOTIFICATION_CANCEL_ERROR');
                case 'SCHEDULED':
                    return translate(action === 'ADD' ? 'RECORDING_NOTIFICATION_SCHEDULE_ERROR' : 'RECORDING_NOTIFICATION_CANCEL_ERROR');
                default:
                    return null;
            }
        }
    };

    return (type: RecordingType, action: 'ADD' | 'REMOVE', callback: () => void, err = false) => {
        addToast(
            {
                title: getTitle(type, action, err),
                message: null,
                ico: getRecordingIcon(type, action, err),
            } as unknown as React.ReactNode,
            {
                appearance: 'success',
                autoDismiss: true,
            },
            callback
        );
    };
};

export const useAriaOnlyNotification = () => {
    const { addToast } = useToasts();

    return (title: string) => {
        addToast(
            {
                title,
                message: null,
                isHidden: true,
            } as unknown as React.ReactNode,
            {
                appearance: 'success',
                autoDismiss: true,
            }
        );
    };
};
